
import { createClient } from '@supabase/supabase-js';
import { Database } from '@/integrations/supabase/types';
import { AdminState } from '@/types/adminTypes';
import { Attendee } from '@/types/attendee';
import { RegistrationFormValues } from '@/schemas/registrationSchema';

// Type imports
import type { AdminAction } from '@/types/adminTypes';

// Supabase configuration with longer timeout
const supabaseUrl = 'https://qfxvvnnqeewyrmqujgrf.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFmeHZ2bm5xZWV3eXJtcXVqZ3JmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDMzMTY0NTIsImV4cCI6MjA1ODg5MjQ1Mn0.A7L6_zboVBzYwApvb-VqQ4KOsHcyuxfnstjsV171t7g';

// Create Supabase client with enhanced configuration
console.log('Creating Supabase client with URL:', supabaseUrl);

// Extended client options for better reliability
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: true
  },
  global: {
    headers: {
      'x-application-name': 'AttendeesApp',
      'x-client-info': 'attendance-app'
    }
  },
  db: {
    schema: 'public'
  },
  // Enhanced realtime config
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

console.log('Supabase client created successfully');

// Database interfaces
export interface Tables {
  attendees: Attendee;
  admin_actions: AdminAction;
  admin_state: AdminState;
}

// Column name mappings for conversion between camelCase and snake_case
export const columnMappings = {
  checkedInAt: 'checked_in_at',
  registeredAt: 'registered_at'
};

// Add the missing registerAttendee function
export const registerAttendee = async (formData: RegistrationFormValues) => {
  if (!formData) {
    console.error('Cannot register attendee: No form data provided');
    return { error: 'No form data provided' };
  }

  try {
    console.log('Registering attendee with data:', formData);
    
    // Calculate total contribution amount
    const fixedAmount = 500000;
    const additionalAmount = formData.additionalContribution ? 
      Number(formData.additionalContribution) : 0;
    const totalAmount = fixedAmount + additionalAmount;

    // Create attendee record
    const { data, error } = await supabase
      .from('attendees')
      .insert({
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        class: formData.class,
        contribution: totalAmount,
        code: formData.code || '', // Use provided code or empty string
        t_shirt_size: formData.t_shirt_size,
        attended: false,
        registered_at: new Date().toISOString(),
        paid: false // Default to unpaid
      })
      .select();

    if (error) {
      console.error('Registration error:', error);
      return { error: error.message };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Registration exception:', error);
    return { error: 'An unexpected error occurred during registration' };
  }
};

// Improved update helper function with multi-attempt approach for all LHP codes
export const updateAttendance = async (code: string, attended: boolean, checkedInAt: string | null) => {
  if (!code) {
    console.error('Cannot update attendance: No code provided');
    return { success: false, error: 'No code provided' };
  }

  console.log(`CRITICAL UPDATE: Updating attendance for ${code} with attended=${attended} and checked_in_at=${checkedInAt}`);
  
  try {
    // Format the timestamp for PostgreSQL TIMESTAMP WITH TIME ZONE
    const timestamp = checkedInAt ? new Date(checkedInAt).toISOString() : null;
    console.log(`Formatted timestamp for database: ${timestamp || 'NULL'}`);
    
    // First attempt: Try to use the RPC function
    if (attended && timestamp) {
      console.log(`Using RPC function for code: ${code}`);
      try {
        const { data: rpcData, error: rpcError } = await supabase
          .rpc('confirm_attendance', { 
            attendee_code: code,
            check_in_time: timestamp 
          });
        
        if (!rpcError && rpcData) {
          console.log('DATABASE UPDATE SUCCESS VIA RPC:', rpcData);
          
          // Get the updated attendee record
          const { data: verifyData } = await supabase
            .from('attendees')
            .select('*')
            .eq('code', code)
            .single();
            
          if (verifyData && verifyData.attended) {
            // Create updated attendee object for frontend with correct camelCase
            const updatedAttendee = {
              code: verifyData.code,
              name: verifyData.name,
              email: verifyData.email,
              phone: verifyData.phone,
              class: verifyData.class,
              contribution: verifyData.contribution,
              registeredAt: verifyData.registered_at,
              attended: Boolean(verifyData.attended),
              checkedInAt: verifyData.checked_in_at,
              paid: Boolean(verifyData.paid)
            };
            
            // Dispatch events
            dispatchUpdateEvents(updatedAttendee, code, timestamp);
            
            return { success: true, data: updatedAttendee };
          }
        }
      } catch (rpcException) {
        console.error('RPC EXCEPTION:', rpcException);
      }
    }
    
    // Second attempt: Direct update
    console.log(`Using direct update for code: ${code}, attended: ${attended}`);
    
    const updateData = attended ? 
      { attended: true, checked_in_at: timestamp } : 
      { attended: false, checked_in_at: null };
    
    const { data: directData, error: directError } = await supabase
      .from('attendees')
      .update(updateData)
      .eq('code', code)
      .select('*');
    
    if (!directError && directData && directData.length > 0) {
      console.log('DIRECT UPDATE SUCCESS:', directData[0]);
      
      const updatedAttendee = {
        code: directData[0].code,
        name: directData[0].name,
        email: directData[0].email,
        phone: directData[0].phone,
        class: directData[0].class,
        contribution: directData[0].contribution,
        registeredAt: directData[0].registered_at,
        attended: Boolean(directData[0].attended),
        checkedInAt: directData[0].checked_in_at,
        paid: Boolean(directData[0].paid)
      };
      
      // Only dispatch events when checking in, not when clearing attendance
      if (attended && timestamp) {
        dispatchUpdateEvents(updatedAttendee, code, timestamp);
      } else {
        // For clearing attendance, dispatch a special event
        const clearEvent = new CustomEvent('attendance-clear', {
          detail: { 
            attendeeCode: code,
            updatedAttendee: updatedAttendee
          }
        });
        window.dispatchEvent(clearEvent);
      }
      
      return { success: true, data: updatedAttendee };
    } else if (directError) {
      console.error('DIRECT UPDATE ERROR:', directError);
    }
    
    // Third attempt: Upsert as last resort
    console.log(`Trying upsert as last resort for code: ${code}`);
    
    // Renamed from upsertData to upsertPayload to avoid conflict
    const upsertPayload = attended ?
      { code: code, attended: true, checked_in_at: timestamp } :
      { code: code, attended: false, checked_in_at: null };
      
    const { data: upsertResult, error: upsertError } = await supabase
      .from('attendees')
      .upsert(upsertPayload, { 
        onConflict: 'code',
        ignoreDuplicates: false
      })
      .select('*');
      
    if (!upsertError && upsertResult && Array.isArray(upsertResult) && upsertResult.length > 0) {
      console.log('UPSERT UPDATE SUCCESS:', upsertResult[0]);
      
      const updatedAttendee = {
        code: upsertResult[0].code,
        name: upsertResult[0].name,
        email: upsertResult[0].email,
        phone: upsertResult[0].phone,
        class: upsertResult[0].class,
        contribution: upsertResult[0].contribution,
        registeredAt: upsertResult[0].registered_at,
        attended: Boolean(upsertResult[0].attended),
        checkedInAt: upsertResult[0].checked_in_at,
        paid: Boolean(upsertResult[0].paid)
      };
      
      // Only dispatch events when checking in, not when clearing attendance
      if (attended && timestamp) {
        dispatchUpdateEvents(updatedAttendee, code, timestamp);
      } else {
        // For clearing attendance
        const clearEvent = new CustomEvent('attendance-clear', {
          detail: { 
            attendeeCode: code,
            updatedAttendee: updatedAttendee
          }
        });
        window.dispatchEvent(clearEvent);
      }
      
      return { success: true, data: updatedAttendee };
    }
    
    console.error('ALL UPDATE ATTEMPTS FAILED FOR CODE:', code);
    return { success: false, error: 'Failed to update using multiple methods' };
    
  } catch (e) {
    console.error('DATABASE UPDATE EXCEPTION:', e);
    return { success: false, error: e };
  }
};

// Helper function to dispatch update events with enhanced detail
function dispatchUpdateEvents(attendee: any, code: string, timestamp: string) {
  // Emit multiple event types for different listeners
  
  // Standard confirmation event with full attendee object
  const confirmEvent = new CustomEvent('attendance-confirmed', {
    detail: {
      attendee,
      timestamp
    }
  });
  window.dispatchEvent(confirmEvent);
  
  // Database success event for compatibility
  const updateEvent = new CustomEvent('database-update-success', {
    detail: { 
      attendeeCode: code, 
      timestamp: timestamp,
      attended: true, 
      updatedAttendee: attendee
    }
  });
  window.dispatchEvent(updateEvent);
  
  // Force UI refresh event
  const refreshEvent = new CustomEvent('attendance-ui-update');
  window.dispatchEvent(refreshEvent);
  
  // Newer format event for direct compatibility with hooked elements
  const newFormatEvent = new CustomEvent('data-sync-completed', {
    detail: { 
      timestamp: new Date().toLocaleTimeString('vi-VN'),
      changedData: true,
      attendeeCode: code,
      attendee: attendee
    }
  });
  window.dispatchEvent(newFormatEvent);
  
  // Direct confirmation event
  const directEvent = new CustomEvent('attendance-direct-confirmed', {
    detail: { 
      attendee,
      timestamp
    }
  });
  window.dispatchEvent(directEvent);
}

// Re-export types
export type { Attendee, AdminAction, AdminState };
